import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Play, FileText, MessageSquare, ArrowLeft } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Media = () => {
  const navigate = useNavigate();
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [viewMode, setViewMode] = useState('article'); // 'article' | 'commentary'

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/podcast/public/episodes`);
        if (res.ok) {
          const data = await res.json();
          setEpisodes(data.episodes || []);
        }
      } catch (e) { console.error(e); }
      setLoading(false);
    })();
  }, []);

  const selectedEp = selected ? episodes.find(e => e.episode_id === selected) : null;

  if (selectedEp) {
    return (
      <div className="min-h-screen bg-[#060503] text-amber-100">
        <Header />
        <div className="max-w-4xl mx-auto px-4 py-8">
          <button onClick={() => setSelected(null)} className="flex items-center gap-2 text-amber-600 hover:text-amber-400 transition-colors text-sm mb-6" data-testid="back-to-episodes">
            <ArrowLeft className="w-4 h-4" /> All Episodes
          </button>

          {/* Video */}
          {selectedEp.show_video_public && (
            <div className="rounded-xl overflow-hidden mb-6 border border-amber-900/20">
              <video
                src={`${API_URL}/api/podcast/stream/${selectedEp.episode_id}`}
                controls
                className="w-full"
                data-testid="episode-video"
              />
            </div>
          )}

          {/* Title */}
          <h1 className="text-2xl sm:text-3xl font-serif font-bold text-amber-200 mb-2" data-testid="episode-title">{selectedEp.title}</h1>
          {selectedEp.series && (
            <span className="inline-block px-2 py-0.5 rounded text-[10px] font-mono text-amber-500 bg-amber-900/20 border border-amber-800/20 mb-4">{selectedEp.series}</span>
          )}
          {selectedEp.description && (
            <p className="text-sm text-amber-400/60 font-serif mb-6 leading-relaxed">{selectedEp.description}</p>
          )}

          {/* View mode toggle */}
          {selectedEp.article_rewrite && selectedEp.commentary_rewrite && (
            <div className="flex gap-2 mb-6">
              <button
                onClick={() => setViewMode('article')}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-mono border transition-colors ${
                  viewMode === 'article' ? 'bg-amber-900/20 border-amber-600/40 text-amber-300' : 'border-amber-900/20 text-amber-600/50 hover:text-amber-500'
                }`}
                data-testid="view-article"
              >
                <FileText className="w-3 h-3" /> Full Article
              </button>
              <button
                onClick={() => setViewMode('commentary')}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-mono border transition-colors ${
                  viewMode === 'commentary' ? 'bg-amber-900/20 border-amber-600/40 text-amber-300' : 'border-amber-900/20 text-amber-600/50 hover:text-amber-500'
                }`}
                data-testid="view-commentary"
              >
                <MessageSquare className="w-3 h-3" /> Commentary
              </button>
            </div>
          )}

          {/* Content */}
          <div className="prose prose-invert prose-amber max-w-none">
            {viewMode === 'article' && selectedEp.article_rewrite ? (
              <div className="text-sm font-serif text-amber-300/80 leading-relaxed whitespace-pre-wrap" data-testid="episode-article">
                {selectedEp.article_rewrite}
              </div>
            ) : viewMode === 'commentary' && selectedEp.commentary_rewrite ? (
              <div className="text-sm font-serif text-amber-300/80 leading-relaxed whitespace-pre-wrap" data-testid="episode-commentary">
                {selectedEp.commentary_rewrite}
              </div>
            ) : selectedEp.article_rewrite ? (
              <div className="text-sm font-serif text-amber-300/80 leading-relaxed whitespace-pre-wrap" data-testid="episode-article">
                {selectedEp.article_rewrite}
              </div>
            ) : selectedEp.commentary_rewrite ? (
              <div className="text-sm font-serif text-amber-300/80 leading-relaxed whitespace-pre-wrap" data-testid="episode-commentary">
                {selectedEp.commentary_rewrite}
              </div>
            ) : (
              <p className="text-amber-700/50">No content available yet.</p>
            )}
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#060503] text-amber-100">
      <Header />

      {/* Hero */}
      <div className="relative py-16 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-amber-600/60 font-serif text-xs tracking-[0.4em] uppercase mb-3">Trump of the Lord Ministries</p>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold mb-4"
            style={{ background: 'linear-gradient(180deg, #fcd34d 0%, #d97706 50%, #92400e 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            Media Library
          </h1>
          <p className="text-amber-500/50 text-sm max-w-lg mx-auto leading-relaxed font-serif">
            Video responses, teaching episodes, and commentary from the GIT Protocol perspective
          </p>
        </div>
      </div>

      {/* Episodes */}
      <div className="max-w-5xl mx-auto px-4 pb-16">
        {loading ? (
          <div className="text-center py-16 text-amber-700/50 font-serif">Loading episodes...</div>
        ) : episodes.length === 0 ? (
          <div className="text-center py-16">
            <Play className="w-12 h-12 text-amber-800/30 mx-auto mb-4" />
            <p className="text-amber-600/40 font-serif text-sm">No episodes published yet. Check back soon.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {episodes.map(ep => (
              <button
                key={ep.episode_id}
                onClick={() => setSelected(ep.episode_id)}
                className="group text-left rounded-xl border border-amber-900/20 overflow-hidden hover:border-amber-700/40 transition-all bg-[#0a0804]"
                data-testid={`public-episode-${ep.episode_id}`}
              >
                {ep.show_video_public && (
                  <div className="aspect-video bg-black relative">
                    <video
                      src={`${API_URL}/api/podcast/stream/${ep.episode_id}`}
                      className="w-full h-full object-cover"
                      muted
                      preload="metadata"
                    />
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center group-hover:bg-black/20 transition-colors">
                      <Play className="w-10 h-10 text-white/70 group-hover:text-white group-hover:scale-110 transition-all" />
                    </div>
                  </div>
                )}
                <div className="p-5">
                  {ep.series && (
                    <span className="inline-block px-2 py-0.5 rounded text-[9px] font-mono text-amber-500/70 bg-amber-900/15 border border-amber-800/15 mb-2">{ep.series}</span>
                  )}
                  <h3 className="font-serif text-base font-semibold text-amber-200/80 mb-2 group-hover:text-amber-100 transition-colors">{ep.title}</h3>
                  {ep.description && (
                    <p className="text-xs text-amber-400/40 leading-relaxed line-clamp-2">{ep.description}</p>
                  )}
                  <div className="mt-3 flex items-center gap-2">
                    {ep.article_rewrite && (
                      <span className="flex items-center gap-1 text-[9px] font-mono text-amber-600/50">
                        <FileText className="w-3 h-3" /> Article
                      </span>
                    )}
                    {ep.commentary_rewrite && (
                      <span className="flex items-center gap-1 text-[9px] font-mono text-amber-600/50">
                        <MessageSquare className="w-3 h-3" /> Commentary
                      </span>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default Media;
